import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { toast } from 'sonner';

import { humanErrorMessage } from '@/api/errors';
import { ClientFormFields } from '@/clients/client-form';
import {
  useClientsList,
  useCreateClient,
  type ClientRecord,
  type ClientSort,
  type ClientsListFilter,
} from '@/clients/queries';
import { ClientCreateSchema, type ClientCreateInput } from '@/clients/schemas';
import { DataTable } from '@/components/data-table';
import { FormDialog } from '@/components/form-dialog';
import { PageShell } from '@/components/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useListQueryRoute } from '@/lib/use-list-query-route';

const formatDate = (iso: string): string => new Date(iso).toLocaleString('ru-RU');

export const ClientsListPage = () => {
  // Phase 8.5-05 — URL-driven list state (ADR-014).
  const lq = useListQueryRoute<ClientsListFilter, ClientSort>({
    from: '/_protected/clients',
    defaultFilter: {},
    defaultSort: { field: 'createdAt', dir: 'desc' },
  });
  const [createOpen, setCreateOpen] = useState(false);

  const clientsQuery = useClientsList({
    filter: lq.filter,
    sort: lq.sort,
    page: lq.page,
    pageSize: lq.pageSize,
  });
  const items = clientsQuery.data?.items ?? [];
  const total = clientsQuery.data?.total ?? 0;

  const createClient = useCreateClient();

  const columns: ColumnDef<ClientRecord>[] = [
    {
      id: 'name',
      header: 'Название',
      meta: { sortField: 'name' },
      cell: ({ row }) => (
        <Link
          to="/clients/$clientId"
          params={{ clientId: row.original.id }}
          className="font-medium hover:underline"
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      id: 'isInternal',
      header: 'Тип',
      cell: ({ row }) =>
        row.original.isInternal ? <Badge variant="secondary">internal</Badge> : '—',
    },
    {
      id: 'createdAt',
      header: 'Создан',
      meta: { sortField: 'createdAt' },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{formatDate(row.original.createdAt)}</span>
      ),
    },
  ];

  const handleSubmit = (values: ClientCreateInput): void => {
    createClient.mutate(values, {
      onSuccess: () => {
        toast.success('Клиент создан');
        setCreateOpen(false);
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  const filterSlot = (
    <>
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={lq.filter.includeDeleted ?? false}
          onChange={(e) => {
            lq.updateQuery({
              filter: { includeDeleted: e.target.checked ? true : undefined },
            });
          }}
          className="h-4 w-4"
        />
        Показывать удалённых
      </label>
      <span className="text-sm text-muted-foreground">Всего: {total}</span>
    </>
  );

  return (
    <div className="space-y-4 p-6">
      <PageShell
        title="Клиенты"
        actions={
          <Button
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            Создать клиента
          </Button>
        }
      >
        {clientsQuery.isLoading ? (
          <div className="p-8 text-center text-muted-foreground">Загрузка…</div>
        ) : (
          <DataTable
            columns={columns}
            data={items}
            emptyMessage="Нет клиентов"
            filterSlot={filterSlot}
            sort={{ field: lq.sort.field, dir: lq.sort.dir }}
            onSortChange={(next) => {
              lq.updateQuery({
                sort: { field: next.field as ClientSort | null, dir: next.dir },
              });
            }}
            pagination={{
              page: lq.page,
              pageSize: lq.pageSize,
              total,
              onPageChange: (page) => {
                lq.updateQuery({ page });
              },
            }}
          />
        )}
      </PageShell>

      <FormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Новый клиент"
        schema={ClientCreateSchema}
        defaultValues={{ name: '', description: '', isInternal: false }}
        onSubmit={handleSubmit}
        isPending={createClient.isPending}
      >
        <ClientFormFields />
      </FormDialog>
    </div>
  );
};
